/**
 * useCouponExcelImport — Composable for the admin Excel coupon import flow.
 *
 * Drives the state machine:
 *   idle → uploading → validating → validated → importing → imported
 *                                              ↘ failed
 *
 * Behaviours enforced by spec/design:
 *  - Client-side file guard: only `.xlsx`, ≤ 5 MB; rejected with toast
 *    and NO upload request.
 *  - Upload uses raw `fetch` mirroring `src/modules/admin/ui/FileDropzone.vue`
 *    byte-for-byte: only `Accept` + `Hash-Teatro`, NO manual Content-Type,
 *    NO client upload timeout.
 *  - Polling: 3 s interval, 30 completed GET attempts max, `pollInFlight`
 *    non-overlap, `generation` + `import_id` stale-response guards.
 *  - 422 from upload is file-level — it MUST NOT populate `errores[]`.
 *  - `triggerImport()` guarded by `phase === 'validated' && errores.length === 0`
 *    and sends an explicitly empty body (the action receives only
 *    `{ lista_id, import }`).
 *  - `retry()` performs a full reset: clears timer, bumps `generation`,
 *    clears every ref, returns to idle.
 *  - `onUnmounted` clears the interval.
 *  - `imported` emits exactly once.
 *
 * The composable reads `listaId` from the host component's `$store`
 * (falling back to the module-level singleton) so the modal that hosts
 * it does NOT need a prop.
 */
import { ref, onUnmounted, getCurrentInstance } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useToast } from '@/composables/common/useToast'

const MAX_BYTES = 5 * 1024 * 1024
const POLL_INTERVAL_MS = 3000
const MAX_POLLS = 30

export function useCouponExcelImport() {
    const toast = useToast()

    // Use the store from the host component if available, else fall back to the
    // module-level store. This makes the composable testable with a fake store.
    const instance = getCurrentInstance()
    const hostStore = instance?.proxy?.$store || store

    const phase = ref('idle')
    const importId = ref(null)
    const progress = ref(0)
    const totalFilas = ref(0)
    const totalProcesadas = ref(0)
    const filasConError = ref(0)
    const errores = ref([])
    const mensajeFinal = ref('')
    const loading = ref(false)
    const apiError = ref(null)
    const uploadFileError = ref(null)
    const pollInFlight = ref(false)
    const triggerInFlight = ref(false)
    const cancelInFlight = ref(false)
    const generation = ref(0)
    const attemptCount = ref(0)
    const importedToastFor = ref(null)

    let pollTimer = null

    // ── Helpers ──────────────────────────────────────────────────────────────

    function stopPoll() {
        if (pollTimer) {
            clearInterval(pollTimer)
            pollTimer = null
        }
    }

    function clearAll() {
        stopPoll()
        pollInFlight.value = false
        triggerInFlight.value = false
        cancelInFlight.value = false
        phase.value = 'idle'
        importId.value = null
        progress.value = 0
        totalFilas.value = 0
        totalProcesadas.value = 0
        filasConError.value = 0
        errores.value = []
        mensajeFinal.value = ''
        loading.value = false
        apiError.value = null
        uploadFileError.value = null
        attemptCount.value = 0
        importedToastFor.value = null
    }

    function getApiBase() {
        return config.API_BASE || ''
    }

    function resolveUrl(routeKey) {
        const route = config.getAPIRoute(routeKey)
        if (!route) throw new Error(`Route not found: ${routeKey}`)
        const localListaId = hostStore.getters.getListaId
        const data = { lista_id: localListaId, import: importId.value }
        let url = route.url
        for (const k of Object.keys(data)) {
            url = url.replace('%' + k + '%', data[k])
        }
        // Backend auth scheme (matches `format_url_params` in `src/api.js` and
        // `src/services/admin/adminApiClient.js#withAdminCredentials`):
        // the API requires `api_key` as a query-string parameter on every
        // request. The legacy Vuex pipeline injects it automatically via
        // `api.call`, but because the upload uses raw `fetch` (the JSON
        // pipeline hardcodes `Content-Type: application/json`), we must append
        // it manually here, along with `tid` (= `hash_teatro`) if present.
        const token = localStorage.getItem('token') || ''
        const hashTeatro = localStorage.getItem('hash_teatro') || ''
        const separator = url.includes('?') ? '&' : '?'
        const params = []
        if (token) params.push('api_key=' + encodeURIComponent(token))
        if (hashTeatro) params.push('tid=' + encodeURIComponent(hashTeatro))
        return getApiBase() + url + (params.length ? separator + params.join('&') : '')
    }

    function failUpload(message) {
        apiError.value = message
        phase.value = 'failed'
        loading.value = false
    }

    /**
     * Unwrap Laravel's `{ data: { ... } }` envelope (or accept a raw payload)
     * so the rest of the composable can work with the canonical
     * `ImportEstadoResource` fields directly.
     */
    function unwrapBackendPayload(body) {
        if (!body) return null
        if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
            return body.data
        }
        return body
    }

    /**
     * Re-hydrate the composable state from a status payload. Used by both
     * the polling tick and the 409 conflict path on upload, so the
     * "import already in flight" scenario resumes the in-flight flow
     * instead of leaving the user stuck on a generic failed state.
     */
    function applyStatusPayload(data) {
        if (typeof data.id !== 'undefined' && data.id !== null) {
            importId.value = data.id
        }
        totalFilas.value = data.total_rows || 0
        totalProcesadas.value = data.processed_rows || 0
        filasConError.value = data.error_count || 0
        progress.value = typeof data.progress === 'number' ? data.progress : 0
        errores.value = Array.isArray(data.errors) ? data.errors : []
        mensajeFinal.value = data.terminal_message || ''

        const backendStatus = data.status
        if (backendStatus === 'imported' || backendStatus === 'failed') {
            stopPoll()
        }

        if (backendStatus === 'validated') {
            phase.value = 'validated'
            stopPoll()
        } else if (backendStatus === 'importing') {
            phase.value = 'importing'
        } else if (backendStatus === 'imported') {
            phase.value = 'imported'
            stopPoll()
            // Surface a success toast exactly once per import so the user
            // gets confirmation feedback even if the modal closes
            // immediately (the modal auto-closes on imported and the
            // banner is only visible for a flash). The `importedToastFor`
            // memo prevents double-toasting if a stale status response
            // re-applies the same payload.
            if (importedToastFor.value !== importId.value) {
                importedToastFor.value = importId.value
                toast.success('Cupones importados correctamente.')
            }
        } else if (backendStatus === 'failed') {
            phase.value = 'failed'
            apiError.value = mensajeFinal.value || 'Falló la importación'
            stopPoll()
        } else if (backendStatus === 'validating') {
            phase.value = 'validating'
        }
    }

    // ── uploadFile(file) ─────────────────────────────────────────────────────

    async function uploadFile(file) {
        uploadFileError.value = null
        apiError.value = null

        const name = (file && file.name) || ''
        const lowerName = name.toLowerCase()
        if (!lowerName.endsWith('.xlsx')) {
            uploadFileError.value = 'xlsx_only'
            toast.error('Solo se permiten archivos .xlsx')
            return
        }
        if (file.size > MAX_BYTES) {
            uploadFileError.value = 'size_exceeded'
            toast.error('El archivo supera el tamaño máximo de 5 MB')
            return
        }

        loading.value = true
        phase.value = 'uploading'

        const url = resolveUrl('back.lista.cupones.cargar.excel')
        const formData = new FormData()
        // Backend expects the multipart field name to be `file` (see
        // `CargarExcelCuponesRequest::rules()` in the api1 repo:
        // `'file' => [..., 'required', ...]`, and the controller calls
        // `$request->file('file')`). Sending it as `archivo` returns
        // `[ "El archivo Excel es obligatorio." ]`.
        formData.append('file', file)

        // Stale-response guard: capture `generation` before the fetch so
        // we can bail out if `retry()` (or any other code path) bumps it
        // while the upload is in flight. Without this guard a slow upload
        // that resolves AFTER the user clicked Reintentar would clobber the
        // freshly-reset state and either resume polling against a dead
        // importId or push the modal back to a half-baked `validating`.
        const genAtStart = generation.value

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: formData,
                headers: {
                    Accept: 'application/json',
                    'Hash-Teatro': localStorage.getItem('hash_teatro') || ''
                }
            })

            let body = null
            try {
                body = await response.json()
            } catch (e) {
                body = null
            }

            // Drop late responses: if `retry()` ran while we were awaiting
            // the fetch, `clearAll()` has already wiped every ref and the
            // caller is no longer interested in this upload.
            if (genAtStart !== generation.value) return

            if (response.status === 202) {
                // Laravel's JsonResource wrapper: the upload endpoint may
                // respond with the bare `{ import_id, status }` OR with
                // `{ data: { import_id, status } }` (mirrors the 409 path
                // and `ImportEstadoResource`). Use the existing
                // `unwrapBackendPayload` helper so both shapes work.
                const unwrapped = unwrapBackendPayload(body)
                importId.value = (unwrapped && (unwrapped.import_id || unwrapped.id)) || null
                generation.value += 1
                phase.value = 'validating'
                loading.value = false
                startPoll()
                return
            }

            // 409 Conflict: there is already a validating/validated/importing
            // import for this user+list. The backend re-renders the full
            // `ImportEstadoResource` payload (with an extra top-level
            // `message`) so the user can resume the in-flight flow: see
            // progress, fix rows, or trigger the import. We re-hydrate the
            // composable from that payload and start polling so the modal
            // catches up with the server-side state.
            if (response.status === 409) {
                const existing = unwrapBackendPayload(body)
                if (existing) {
                    applyStatusPayload(existing)
                    // If the existing import is already terminal, do not
                    // start a poll loop. Otherwise, start polling so we
                    // catch up with whatever the backend is doing.
                    if (existing.status === 'validated' || existing.status === 'importing') {
                        startPoll()
                    } else {
                        // `failed` / `imported`: stopPoll already called by
                        // applyStatusPayload, just make sure the file
                        // picker is back so the user can upload a new one
                        // if they want to.
                        apiError.value = (body && body.message) || apiError.value
                    }
                } else {
                    apiError.value = (body && body.message) || 'Ya hay una importación en curso para esta lista.'
                    phase.value = 'failed'
                }
                loading.value = false
                return
            }

            // 401/403/404/422 → failed with apiError; 422 is FILE-level, never populates errores[]
            if (response.status === 401 || response.status === 403 || response.status === 404) {
                failUpload((body && body.message) || `Error ${response.status}`)
                return
            }
            if (response.status === 422) {
                failUpload((body && body.message) || 'Archivo no válido')
                return
            }

            failUpload((body && body.message) || `Error ${response.status}`)
        } catch (err) {
            if (genAtStart !== generation.value) return
            failUpload(err?.message || 'Error de red')
        }
    }

    // ── polling ──────────────────────────────────────────────────────────────

    function startPoll() {
        stopPoll()
        attemptCount.value = 0
        pollTimer = setInterval(tickPoll, POLL_INTERVAL_MS)
    }

    async function tickPoll() {
        // Safety net: if the interval was cleared (terminal status, retry,
        // unmount) the timer handle is null. Bail out before doing anything
        // so we don't issue a request after the loop has been stopped.
        if (!pollTimer) return
        if (pollInFlight.value) return
        if (attemptCount.value >= MAX_POLLS) {
            stopPoll()
            phase.value = 'failed'
            apiError.value = 'timeout'
            return
        }
        if (phase.value !== 'validating' && phase.value !== 'importing') {
            stopPoll()
            return
        }

        const genAtStart = generation.value
        const idAtStart = importId.value
        if (!idAtStart) return

        pollInFlight.value = true
        attemptCount.value += 1

        try {
            const res = await hostStore.dispatch(
                config.getVuexAlias('back.lista.cupones.importar.status'),
                { lista_id: hostStore.getters.getListaId, import: idAtStart }
            )
            // Stale-response guard: if generation or import_id changed, drop this response.
            if (genAtStart !== generation.value || idAtStart !== importId.value) return

            // Laravel's JsonResource wrapper: the controller returns
            // `new ImportEstadoResource($import)`, which axios surfaces as
            // `{ data: { id, status, ... } }`. The unit-test stubs pass the
            // payload directly (no wrapper), so we accept both shapes.
            const raw = res && res.data
            const data = (raw && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data))
                ? raw.data
                : raw
            if (!data) return

            // The backend resource uses its own field names; the composable
            // normalises them onto stable internal refs so the modal/UX is
            // decoupled from the transport vocabulary.
            //   id              → importId
            //   total_rows      → totalFilas
            //   processed_rows  → totalProcesadas
            //   error_count     → filasConError
            //   errors[]        → errores[]   (row_number / column / code / message)
            //   terminal_message → mensajeFinal
            //   progress        → progress (0..100)
            if (typeof data.id !== 'undefined' && data.id !== null) {
                importId.value = data.id
            }
            totalFilas.value = data.total_rows || 0
            totalProcesadas.value = data.processed_rows || 0
            filasConError.value = data.error_count || 0
            progress.value = typeof data.progress === 'number' ? data.progress : 0
            errores.value = Array.isArray(data.errors) ? data.errors : []
            mensajeFinal.value = data.terminal_message || ''

            const backendStatus = data.status

            // Backstop: if the backend has already processed every row but
            // has not flipped status yet, stop polling and surface a useful
            // reason. The backend has a strict state machine
            // (validating → validated/failed → importing → imported/failed)
            // so a real terminal state should be reflected on the next tick;
            // this guard prevents the front from looping on a stuck import
            // and prevents the user from clicking Importar twice.
            if (
                totalFilas.value > 0
                && totalProcesadas.value === totalFilas.value
                && (backendStatus === 'validating' || backendStatus === 'importing')
            ) {
                stopPoll()
                if (filasConError.value > 0) {
                    phase.value = 'validated'
                } else {
                    phase.value = 'failed'
                    apiError.value = 'El servidor no ha notificado el estado final. Reintenta para ver el resultado.'
                }
                return
            }

            applyStatusPayload(data)
        } catch (err) {
            // Stale-response guard before applying error
            if (genAtStart !== generation.value || idAtStart !== importId.value) return
            const status = err?.response?.status
            const msg = err?.response?.data?.message || err?.message || 'Error al consultar el estado'
            if (status === 403 || status === 404) {
                apiError.value = msg
                phase.value = 'failed'
                stopPoll()
            } else {
                // Network blip: keep polling, but limit attempts (attemptCount already incremented)
                if (attemptCount.value >= MAX_POLLS) {
                    apiError.value = 'timeout'
                    phase.value = 'failed'
                    stopPoll()
                }
            }
        } finally {
            pollInFlight.value = false
        }
    }

    // ── triggerImport ────────────────────────────────────────────────────────

    async function triggerImport() {
        // Hard guards: only callable when we are validated with zero hard
        // errors and an importId is set. The 409 conflict path lands here
        // too: the composable was hydrated from the existing validated
        // payload, so `importId` is already populated and the user just
        // needs to confirm the import.
        //
        // IMPORTANT: gate on `filasConError` (the backend's `error_count`,
        // i.e. errors that BLOCK the import), NOT on `errores.length`. The
        // backend can ship soft warnings in `errors[]` (e.g.
        // `looks_like_truncated_dni`) with `error_count: 0`; those are
        // advisory and must not block the import.
        if (phase.value !== 'validated') {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[useCouponExcelImport] triggerImport: phase is', phase.value, 'expected validated')
            }
            return
        }
        if (filasConError.value > 0) return
        if (!importId.value) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[useCouponExcelImport] triggerImport: importId is empty')
            }
            return
        }
        // Single in-flight guard: avoid double-dispatching while the
        // backend round-trip is in progress. The backend is already
        // idempotent (409 on re-dispatch), so we do NOT need a
        // per-importId memo.
        if (triggerInFlight.value) return

        triggerInFlight.value = true
        const genAtStart = generation.value
        try {
            const res = await hostStore.dispatch(
                config.getVuexAlias('back.lista.cupones.importar.trigger'),
                { lista_id: hostStore.getters.getListaId, import: importId.value }
            )
            if (genAtStart !== generation.value) return
            phase.value = 'importing'
            startPoll()
        } catch (err) {
            if (genAtStart !== generation.value) return
            const status = err?.response?.status
            const msg = err?.response?.data?.message || err?.message || 'Error al iniciar la importación'
            apiError.value = msg
            // 409 (already importing / already imported) is not a hard
            // failure: keep polling, the next status response will tell us
            // where the import actually is.
            if (status === 409) {
                phase.value = 'importing'
                startPoll()
            } else {
                phase.value = 'failed'
                stopPoll()
            }
        } finally {
            triggerInFlight.value = false
        }
    }

    // ── cancelImport ───────────────────────────────────────────────────────

    /**
     * Cancel the current in-flight / validated import via the DELETE
     * endpoint. The backend marks the import as `failed` with a terminal
     * message, deletes the uploaded workbook, and dispatches the
     * cancellation notification. On success we go back to `idle` so
     * the user can pick a fresh file.
     *
     * Only callable when the composable is in a `validated` or
     * `validating` state (i.e. the backend can still cancel it).
     */
    async function cancelImport() {
        // Accept 'failed' too: the failed phase can leave a residual
        // importId on the server that the user needs cleared before
        // uploading another file. The `failed` branch in the modal
        // exposes "Reintentar" as the entry point for that cleanup.
        if (
            phase.value !== 'validated'
            && phase.value !== 'validating'
            && phase.value !== 'failed'
        ) return
        if (!importId.value) return
        if (cancelInFlight.value) return

        cancelInFlight.value = true
        const genAtStart = generation.value
        try {
            await hostStore.dispatch(
                config.getVuexAlias('back.lista.cupones.importar.cancel'),
                { lista_id: hostStore.getters.getListaId, import: importId.value }
            )
            if (genAtStart !== generation.value) return
            // After a successful cancellation, the import is gone for good.
            // Reset the composable so the user can pick a new file. The
            // backend's cancellation notification takes care of the toast.
            generation.value += 1
            clearAll()
        } catch (err) {
            if (genAtStart !== generation.value) return
            const status = err?.response?.status
            const msg = err?.response?.data?.message || err?.message || 'No se ha podido cancelar la importación.'
            apiError.value = msg
            phase.value = 'failed'
            stopPoll()
        } finally {
            cancelInFlight.value = false
        }
    }

    // ── retry ────────────────────────────────────────────────────────────────

    function retry() {
        generation.value += 1
        clearAll()
    }

    // ── cleanup ──────────────────────────────────────────────────────────────

    onUnmounted(() => {
        stopPoll()
    })

    return {
        // state
        phase,
        importId,
        progress,
        totalFilas,
        totalProcesadas,
        filasConError,
        errores,
        mensajeFinal,
        loading,
        apiError,
        uploadFileError,
        pollInFlight,
        cancelInFlight,
        generation,
        attemptCount,
        // actions
        uploadFile,
        triggerImport,
        cancelImport,
        retry
    }
}
